import { IRecipientEntity } from '../../model/api/recipients/recipient.entity.interface';
import { IRecipient } from '../../model/models/recipient/'
import { StorageType } from '../../model/types/storage-type';
import { StorageProvider } from '../storage/storage';
import { IRecipientBankSearchRequest } from '../../model/models/recipient/recipient-bank-search-request.interface';
import { IRecipientBank } from '../../model/models/recipient/recipient-bank.interface';
import { IRecipientBankSearchRequestEntity } from '../../model/api/recipients/recipient-bank-search-request.entity.interface';
import { IRecipientBankSearchResponseEntity } from '../../model/api/recipients/recipient-bank-search-response.entity.interface';
import { ISaveRecipientBankDepositRequestEntity } from '../../model/api/recipients/save-recipient-bank-deposit-request.entity.interface';
import { IRecipientBankEntity } from '../../model/api/recipients/recipient-bank.entity.interface';
import {
  IBankDepositBankSearchRequestEntity,
  IBankDepositBankSearchResponseEntity,
  IBankDepositIsSmartRoutingEnabledRequestEntity,
  IBankDepositSearchEntity
} from '../../model/api/bank-deposit';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { OrderMapper } from '../order/order.mapper';
import { VopConsent } from '../../model/types/vop-type';
import { VirtualPaymentType, BankAccountFormatId } from '../../model/types/vpa-type';

/** Page size the CST banksearch endpoint is called with. */
const BANK_DEPOSIT_SEARCH_PAGE_SIZE = 20;

export class RecipientMapper {

  public static fromRecipientsEntity(recipientEntities: IRecipientEntity[], storageProvider: StorageProvider = null): IRecipient[] {
    return recipientEntities.map(recipient => { return this.fromRecipientEntity(recipient, storageProvider) });
  }

  public static fromRecipientEntity(entity: IRecipientEntity, storageProvider: StorageProvider = null): IRecipient {
    const recipient = {
      firstName: entity.firstName,
      middleName: entity.middleName,
      lastName1: entity.lastName1,
      lastName2: entity.lastName2,
      address: entity.address,
      city: entity.city,
      state: entity.state,
      postalCode: entity.postalCode,
      emailAddress: entity.emailAddress,
      telephoneNumber: entity.telephoneNumber,
      telephoneNumberCell: entity.telephoneNumberCell,
      idDocumentTypeId: entity.idDocumentTypeId,
      specificIdTypeId: entity.specificIdTypeId,
      idDocumentNumber: entity.idDocumentNumber,
      customerRecipientRelationshipId: entity.customerRecipientRelationshipId,
      customerRecipientRelationshipName: entity.customerRecipientRelationshipName,
      id: entity.id,
      beneficiaryId: entity.beneficiaryId,
      country: entity.country,
      bankId: entity.bankId,
      bankLocationId: entity.bankLocationId,
      bankName: RecipientMapper.formatBankName(entity.bankName),
      bankLocationName: entity.bankLocationName,
      bankLocationNumber: entity.bankLocationNumber,
      bankLocationCity: entity.bankLocationCity,
      bankRoutingTypeId: entity.bankRoutingTypeId,
      bankRoutingType: entity.bankRoutingType,
      bankProviderId: entity.bankProviderId,
      bankAgentId: entity.bankAgentId,
      bankAgentLocationId: entity.bankAgentLocationId,
      bankRoutingCode: entity.bankRoutingCode,
      bankRoutingCodeLabel: entity.bankRoutingCodeLabel,
      bankRoutingCodeFull: entity.bankRoutingCodeFull,
      bankAccountTypeLabel: entity.bankAccountTypeLabel,
      bankAccountNumberUnitaryLabel: entity.bankAccountNumberUnitaryLabel,
      bankAccountNumberUnitaryFull: entity.bankAccountNumberUnitaryFull,
      bankAccountNumber: entity.bankAccountNumber,
      bankAccountNumberFull: entity.bankAccountNumberFull,
      bankAccountTypeId: entity.bankAccountTypeId,
      bankAccountType: entity.bankAccountType,
      bankAccountNumberUnitary: entity.bankAccountNumberUnitary,
      bankAccountTypeUnitary: entity.bankAccountTypeUnitary,
      bankAccountFormatId: entity.bankAccountFormatId,
      bankBicSwift: entity.bankBicSwift,
      image: null,
      fullName: entity.firstName,
      mobileWalletAccountNo: entity.mobileWalletAccountNo,
      virtualPaymentAccountNo: entity.virtualPaymentAccountNo,
      virtualPaymentType: entity.virtualPaymentType,
      virtualPaymentAccountNoPrefix: entity.virtualPaymentAccountNoPrefix,
      isVpa: entity.isVpa,
      pixIdType: entity.pixIdType,
      vpaIdType: entity.vpaIdType,
      lastDeliveryMethod: entity.lastDeliveryMethod
    }
    if (entity.middleName)
      recipient.fullName += ' ' + entity.middleName;
    if (entity.lastName1)
      recipient.fullName += ' ' + entity.lastName1;
    if (entity.lastName2)
      recipient.fullName += '-' + entity.lastName2;

    /*
    if (storageProvider) {
      storageProvider.getImage(StorageType.RecipientImage + recipient.id)
        .then((image) => {
          recipient.image = image;
        })
        .catch(() => { });
    }
    */

    if (recipient.lastDeliveryMethod === DeliveryMethodType.BankDeposit && RecipientMapper.isDirectDebitPayoutAgent(recipient)) {
      recipient.lastDeliveryMethod = DeliveryMethodType.DebitCard;
    }

    return recipient;
  }

  public static toSaveRecipientBankDepositRequestEntity(model: IRecipient, currencyTo: string, amountFrom: number, deliveryMethod = null, vopConsent = null, vopFingerprint = null): ISaveRecipientBankDepositRequestEntity {
    const recipient = {
      currencyTo,
      amountFrom,
      vopConsent: vopConsent || VopConsent.None,
      vopFingerprint: vopFingerprint || null,

      firstName: model.firstName,
      middleName: model.middleName,
      lastName1: model.lastName1,
      lastName2: model.lastName2,
      address: model.address,
      city: model.city,
      state: model.state,
      postalCode: model.postalCode,
      emailAddress: model.emailAddress,
      telephoneNumber: model.telephoneNumber,
      telephoneNumberCell: model.telephoneNumberCell,
      idDocumentTypeId: model.idDocumentTypeId,
      specificIdTypeId: model.specificIdTypeId,
      idDocumentNumber: model.idDocumentNumber,
      customerRecipientRelationshipId: model.customerRecipientRelationshipId,
      customerRecipientRelationshipName: model.customerRecipientRelationshipName,
      id: model.id,
      beneficiaryId: model.beneficiaryId,
      country: model.country,
      bankId: model.bankId,
      bankLocationId: model.bankLocationId,
      bankName: RecipientMapper.formatBankName(model.bankName),
      bankLocationName: model.bankLocationName,
      bankLocationNumber: model.bankLocationNumber,
      bankLocationCity: model.bankLocationCity,
      bankRoutingTypeId: model.bankRoutingTypeId,
      bankRoutingType: model.bankRoutingType,
      bankProviderId: model.bankProviderId,
      bankAgentId: model.bankAgentId,
      bankAgentLocationId: model.bankAgentLocationId,
      isSmartRouting: model.isSmartRouting,
      bankRoutingCode: model.bankRoutingCode,
      bankRoutingCodeLabel: model.bankRoutingCodeLabel,
      bankRoutingCodeFull: model.bankRoutingCodeFull,
      bankAccountTypeLabel: model.bankAccountTypeLabel,
      bankAccountNumberUnitaryLabel: model.bankAccountNumberUnitaryLabel,
      bankAccountNumberUnitaryFull: model.bankAccountNumberUnitaryFull,
      bankAccountNumber: RecipientMapper.resolveBankAccountNumberToSave(model),
      bankAccountNumberFull: model.bankAccountNumberFull,
      bankAccountTypeId: model.bankAccountTypeId,
      bankAccountType: model.bankAccountType,
      bankAccountNumberUnitary: model.bankAccountNumberUnitary,
      bankAccountTypeUnitary: model.bankAccountTypeUnitary,
      bankAccountFormatId: model.bankAccountFormatId,
      bankBicSwift: model.bankBicSwift,
      virtualPaymentAccountNo: model.virtualPaymentAccountNo ? model.virtualPaymentAccountNo : null,
      virtualPaymentType: model.virtualPaymentType ? model.virtualPaymentType : null,
      virtualPaymentAccountNoPrefix: model.virtualPaymentAccountNoPrefix ? model.virtualPaymentAccountNoPrefix : null,
      pixIdType: model.pixIdType ? model.pixIdType : null,
      vpaIdType: model.vpaIdType ? model.vpaIdType : null,
      deliveryMethod,
    }

    return recipient;
  }

  private static resolveBankAccountNumberToSave(model: IRecipient): string {
    const typedUnitaryAccountNumber = model.bankAccountNumberUnitary;

    if (RecipientMapper.isMaskedAccountValue(model.bankAccountNumber)
      && typedUnitaryAccountNumber
      && !RecipientMapper.isMaskedAccountValue(typedUnitaryAccountNumber)) {
      return typedUnitaryAccountNumber;
    }

    return model.bankAccountNumber;
  }

  private static isMaskedAccountValue(value: string): boolean {
    return !!value && value.indexOf('*') >= 0;
  }

  /** Legacy criteria to a banksearch request. bankName/bankCode collapse to searchString, and banksearch pages from 1 where bank/search/v2 pages from 0. */
  public static toBankDepositSearchRequestEntity(searchModel: IRecipientBankSearchRequest, corridor?: IBankDepositIsSmartRoutingEnabledRequestEntity): IBankDepositBankSearchRequestEntity {
    let searchString = searchModel.bankName || searchModel.bankCode;
    if (searchString)
      searchString = searchString.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return {
      countryTo: searchModel.countryTo,
      searchString,
      page: (searchModel.page || 0) + 1,
      pageSize: searchModel.pageSize || BANK_DEPOSIT_SEARCH_PAGE_SIZE,
      // The corridor a bank needs a qualified provider for; without it the list is only country-wide.
      currencyFrom: corridor ? corridor.currencyFrom : undefined,
      currencyTo: corridor ? corridor.currencyTo : undefined,
      stateFrom: corridor ? corridor.stateFrom : undefined,
      stateTo: corridor ? corridor.stateTo : undefined
    };
  }

  public static fromBankDepositSearchEntities(searchResponse: IBankDepositBankSearchResponseEntity, selectedBank?: IRecipientBank): IRecipientBank[] {
    return (searchResponse.EntityList) ? searchResponse.EntityList.map(e => this.fromBankDepositSearchEntity(e, selectedBank)) : [];
  }

  /** A banksearch bank, in the shape the existing bank lists already render. */
  public static fromBankDepositSearchEntity(bankEntity: IBankDepositSearchEntity, selectedBank?: IRecipientBank): IRecipientBank {
    return {
      bankId: bankEntity.EntityID,
      bankName: RecipientMapper.formatBankName(bankEntity.EntityName),
      bankAlias: bankEntity.AliasName,
      bankState: bankEntity.State,
      isSelected: selectedBank ? bankEntity.EntityID === selectedBank.bankId : false,
      matchField: bankEntity.MatchField,
      matchStartPos: bankEntity.MatchStartPos,
      matchValue: RecipientMapper.formatBankName(bankEntity.MatchValue),
      matchedString: bankEntity.MatchedString,
      displayName: RecipientMapper.formatBankName(bankEntity.DisplayName),
      banklogo: bankEntity.LogoPathCDN
    };
  }

  public static toRecipientBankSearchRequestEntity(searchModel: IRecipientBankSearchRequest, isCDM: boolean = false): IRecipientBankSearchRequestEntity {
    let bankName = searchModel.bankName;
    if (bankName)
      bankName = bankName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let bankCode = searchModel.bankCode;
    if (bankCode)
      bankCode = bankCode.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return {
      CdmBankListOnly: isCDM,
      countryTo: searchModel.countryTo,
      bankName,
      bankCode,
      matchFromStart: searchModel.matchFromStart,
      page: searchModel.page,
      pageSize: searchModel.pageSize,
    }
  }

  public static fromRecipientBankEntities(bankEntities: IRecipientBankSearchResponseEntity, selectedBank?: IRecipientBank): IRecipientBank[] {
    return (bankEntities.model) ? bankEntities.model.map(e => this.fromRecipientBankEntity(e, selectedBank)) : [];
  }

  public static fromRecipientBankEntity(bankEntity: IRecipientBankEntity, selectedBank?: IRecipientBank): IRecipientBank {
    return {
      bankId: bankEntity.bankId,
      bankName: RecipientMapper.formatBankName(bankEntity.bankName),
      bankAlias: bankEntity.bankAlias,
      bankState: bankEntity.bankState,
      isSelected: selectedBank ? bankEntity.bankId === selectedBank.bankId : false,
      matchField: bankEntity.matchField,
      matchStartPos: bankEntity.matchStartPos,
      matchValue: RecipientMapper.formatBankName(bankEntity.matchValue),
      matchedString: bankEntity.matchedString,
      displayName: RecipientMapper.formatBankName(bankEntity.displayName),
      banklogo: bankEntity.banklogo,
      accountNumber: bankEntity.accountNumber
    }
  }

  public static toBeneficiaryEntity(recipient : IRecipient): any {
    const beneficiary = {
      Address: recipient.address,
      BankAccountNo: recipient.bankAccountNumber,
      BankCurrency: "",
      BankCountryId: 0,
      City: false,
      CorrespBranchNo: 0,
      CorrespLocName: "",
      CorrespName: "",
      Country: recipient.country,
      Currency: "",
      DeliveryMethod: "",
      DeliveryMethodId: 0,
      FullName: recipient.firstName + " " + recipient.middleName + " " + recipient.lastName1 + " " + recipient.lastName2,
      Id: recipient.id,
      IsOpenNetwork: false,
      IsOpenPaymentCountry: false,
      LastUsed: "",
      MobileWalletAccountNo: 0,
      NameId: "",
      PayAgentId: 0,
      PayAgentLocId: 0,
      PayLocCity: "",
      PayLocCityId: 0,
      PostalCode: recipient.postalCode,
      State: recipient.state,
      TelNo: recipient.telephoneNumber
    }
    return beneficiary;
  }

  public static fromRecipientFields(fields: any, recipient: IRecipient): IRecipient {
    fields.forEach(field => {
      recipient[field.id] = field.value;
    });
    return recipient;
  }

  public static formatBankName(bankName) {
    if (bankName) {
      if (bankName.toLowerCase().indexOf('[debitcard]') >= 0)
        return bankName;

      const whiteList = [
        'of',
        'and',
        'de',
        'e',
        'y',
        'von',
        'el',
        'del',
        'la',
        'lo',
        'der',
        'die',
        'das',
        'und',
        'co',
        'fur',
        'des',
        'bank',
        'nova',
        'corp',
        'abu',
        'open'
      ];
      let orig = bankName;
      bankName = bankName.replace(/\./g, ' ');
      bankName = bankName.replace(/\-/g, ' ');
      const parts = bankName.split(' ');
      bankName = '';
      parts.forEach((part) => {
        let separator = ' ';
        if (bankName.length > 0) {
          if (orig.length >= bankName.length)
            separator = orig[bankName.length];
          bankName += separator;
        }
        if (part.length > 4 || whiteList.indexOf(part.toLowerCase()) >= 0) {
          part = part.toLowerCase();
          part = part.substr(0,1).toUpperCase() + part.substr(1);
        }
        bankName += part;
      });
    }
    return bankName;
  }

  public static isDirectDebitPayoutAgent(recipient: IRecipient) {
    return OrderMapper.isDirectDebitPayoutAgent(recipient.bankAgentId);
  }

  public static hasPagoMovilDetails(recipient: IRecipient): boolean {
    if (!recipient || !recipient.virtualPaymentAccountNo)
      return false;
    return recipient.virtualPaymentType === VirtualPaymentType.PagoMovilString
      || recipient.virtualPaymentType === VirtualPaymentType.PagoMovil
      || Number(recipient.bankAccountFormatId) === BankAccountFormatId.PagoMovil;
  }
}



// WEBPACK FOOTER //
// ./src/providers/recipient/recipient.mapper.ts